import { useEffect, useState, useCallback, useMemo } from 'react'
import { createTLStore, defaultShapeUtils, defaultTools, TLStoreSnapshot, TLRecord } from 'tldraw'
import { supabase, DrawingRoom } from '../lib/supabase'
import { RealtimeChannel } from '@supabase/supabase-js'

export function useSupabaseSync(roomId: string) {
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // 创建 tldraw store
  const store = useMemo(() => {
    return createTLStore({
      shapeUtils: defaultShapeUtils,
      tools: defaultTools,
    })
  }, [])

  // 生成用户ID
  const userId = useMemo(() => {
    return Math.random().toString(36).substring(2, 15)
  }, [])

  // 保存房间数据到 Supabase
  const saveRoomData = useCallback(async (snapshot: TLStoreSnapshot) => {
    try {
      const { error } = await supabase
        .from('drawing_rooms')
        .upsert({
          id: roomId,
          name: `Room ${roomId}`,
          data: snapshot,
          updated_at: new Date().toISOString()
        })

      if (error) {
        console.warn('保存房间数据失败:', error)
        setError('Supabase未配置 - 使用本地模式')
        setIsConnected(false)
      }
    } catch (err) {
      console.warn('保存房间数据异常:', err)
      setError('Supabase未配置 - 使用本地模式')
      setIsConnected(false)
    }
  }, [roomId])

  // 加载房间数据
  const loadRoomData = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('drawing_rooms')
        .select('data')
        .eq('id', roomId)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = 没有找到记录
        console.warn('加载房间数据失败:', error)
        setError('Supabase未配置 - 使用本地模式')
        setIsConnected(false)
        return
      }

      if (data?.data) {
        // 加载快照到 store
        store.loadStoreSnapshot(data.data)
      }
    } catch (err) {
      console.warn('加载房间数据异常:', err)
      setError('Supabase未配置 - 使用本地模式')
      setIsConnected(false)
    }
  }, [roomId, store])

  // 发送事件到其他客户端
  const broadcastEvent = useCallback(async (eventType: string, eventData: any) => {
    try {
      const { error } = await supabase
        .from('drawing_events')
        .insert({
          room_id: roomId,
          event_type: eventType,
          event_data: eventData,
          user_id: userId
        })

      if (error) {
        console.error('广播事件失败:', error)
      }
    } catch (err) {
      console.error('广播事件异常:', err)
    }
  }, [roomId, userId])

  // 设置实时订阅
  useEffect(() => {
    let channel: RealtimeChannel | null = null

    const setupRealtime = async () => {
      try {
        // 首先加载房间数据
        await loadRoomData()

        // 设置实时订阅
        channel = supabase
          .channel(`room:${roomId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'drawing_events',
              filter: `room_id=eq.${roomId}`
            },
            (payload) => {
              const event = payload.new
              // 忽略自己发送的事件
              if (event.user_id === userId) return

              // 处理远程事件
              if (event.event_type === 'store_update' && event.event_data) {
                try {
                  // 应用远程更改到本地 store
                  store.mergeRemoteChanges(() => {
                    if (event.event_data.snapshot) {
                      store.loadStoreSnapshot(event.event_data.snapshot)
                    }
                  })
                } catch (err) {
                  console.error('应用远程更改失败:', err)
                }
              }
            }
          )
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              setIsConnected(true)
              setError(null)
              setIsLoading(false)
            } else if (status === 'CHANNEL_ERROR') {
              setIsConnected(false)
              setError('实时连接失败')
              setIsLoading(false)
            }
          })

      } catch (err) {
        console.error('设置实时连接失败:', err)
        setError('连接失败')
        setIsLoading(false)
      }
    }

    setupRealtime()

    return () => {
      if (channel) {
        channel.unsubscribe()
      }
    }
  }, [roomId, userId, store, loadRoomData])

  // 监听 store 变化并同步
  useEffect(() => {
    let lastSnapshot: TLStoreSnapshot | null = null
    let saveTimeout: NodeJS.Timeout | null = null

    const handleStoreChange = () => {
      const snapshot = store.getStoreSnapshot()
      
      // 防抖保存
      if (saveTimeout) {
        clearTimeout(saveTimeout)
      }
      
      saveTimeout = setTimeout(() => {
        // 检查是否有实际变化
        if (JSON.stringify(snapshot) !== JSON.stringify(lastSnapshot)) {
          lastSnapshot = snapshot
          
          // 保存到数据库
          saveRoomData(snapshot)
          
          // 广播变化给其他客户端
          broadcastEvent('store_update', { snapshot })
        }
      }, 500) // 500ms 防抖
    }

    // 监听 store 变化
    const unsubscribe = store.listen(handleStoreChange)

    return () => {
      unsubscribe()
      if (saveTimeout) {
        clearTimeout(saveTimeout)
      }
    }
  }, [store, saveRoomData, broadcastEvent])

  return {
    store,
    isConnected,
    isLoading,
    error,
    userId
  }
} 